import {
  useStrings,
  useStringsWithPlaceholders,
} from "@1js/localization-components";
import type { Group, Status } from "@1js/pl-types";
import {
  Button,
  makeStyles,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import * as React from "react";
import { isTeamsApp } from "../../services/urlBuilder";
import { NoResults } from "../NoResults/NoResults";
import { PromptTabs } from "../PromptTab/PromptTabs";
import type { Prompt } from "../data";
import {
  PromptGridDescription,
  PromptGridLearnMoreLinkLabel,
  PromptGridNoTeamsSuccessButtonLabel,
  PromptGridNoTeamsSuccessDescription,
  PromptGridNoTeamsSuccessTitle,
  PromptGridTeamsNoPromptsMemberSuccessButtonLabel,
  PromptGridTeamsNoPromptsMemberSuccessTitle,
  PromptGridTeamsNoPromptsOwnerSuccessButtonLabel,
  PromptGridTeamsNoPromptsOwnerSuccessButtonLabelCannotShareV2,
  PromptGridTeamsNoPromptsOwnerSuccessDescriptionCannotShareV2,
  PromptGridTeamsNoPromptsOwnerSuccessTitle,
  PromptsRequestFailed,
} from "./PromptResultsAnnouncement.strings";
import { SharePromptText } from "./SharePromptText";

const COPILOT_LAB_URL = "https://copilot.cloud.microsoft/prompts/all";

const useStyles = makeStyles({
  srOnly: {
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: "auto",
    position: "absolute",
    width: "1px",
    whiteSpace: "nowrap",
    ...shorthands.border("0px"),
    ...shorthands.margin("0px"),
    ...shorthands.overflow("hidden"),
    ...shorthands.padding("0px"),
  },

  container: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    ...shorthands.padding("20px", "0px"),
    "@media (max-width: 540px)": {
      ...shorthands.padding("16px", "0px"),
    },
  },
  text: {
    color: tokens.colorNeutralForeground2,
    textAlign: "center",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: "28px",
    zIndex: 1,
    "@media (max-width: 540px)": {
      fontSize: "16px",
      lineHeight: "22px",
    },
  },
  additionalText: {
    color: tokens.colorNeutralForeground2,
    textAlign: "center",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: tokens.fontWeightRegular,
    lineHeight: "28px",
    zIndex: 1,
    "@media (max-width: 540px)": {
      fontSize: "16px",
      lineHeight: "22px",
    },
  },
  actionButton: {
    marginTop: "20px",
    color: tokens.colorBrandForeground1,
    ...shorthands.border("1px", "solid", tokens.colorBrandForeground1),
  },
});
export type PromptResultsAnnouncementProps = {
  id: string;
  prompts: Prompt[];
  isLoading?: boolean;
  isFailed?: boolean;
  hasBookmarkedPrompts?: boolean;
  selectedTab: PromptTabs;
  groups?: Group[];
  groupPrompts?: Prompt[];
  onExploreOtherPrompts?: () => void;
  onSharePrompts?: () => void;
  onGoToCopilotLabClicked?: () => void;
  groupHasPromptsStatus?: Status;
  groupPromptStatus?: Status;
  isMetaOsV2?: boolean;
};

export const PromptResultsAnnouncement: React.FC<
  PromptResultsAnnouncementProps
> = ({
  id,
  prompts,
  isLoading,
  isFailed,
  hasBookmarkedPrompts,
  groups,
  selectedTab,
  groupPrompts,
  onExploreOtherPrompts,
  onSharePrompts,
  onGoToCopilotLabClicked,
  isMetaOsV2,
}) => {
  const styles = useStyles();
  const localization = useStrings({
    PromptsRequestFailed,
    PromptGridDescription,
    PromptGridNoTeamsSuccessButtonLabel,
    PromptGridNoTeamsSuccessTitle,
    PromptGridTeamsNoPromptsMemberSuccessTitle,
    PromptGridTeamsNoPromptsMemberSuccessButtonLabel,
    PromptGridTeamsNoPromptsOwnerSuccessButtonLabel,
    PromptGridTeamsNoPromptsOwnerSuccessButtonLabelCannotShareV2,
    PromptGridTeamsNoPromptsOwnerSuccessTitle,
    PromptGridTeamsNoPromptsOwnerSuccessDescriptionCannotShareV2,
    PromptGridLearnMoreLinkLabel,
  });
  const localizationWithPlaceholders = useStringsWithPlaceholders({
    PromptGridNoTeamsSuccessDescription,
  });

  const isLoadingOrFailed = isLoading || isFailed;
  const isSuccess = !isLoadingOrFailed;
  const isGroupPromptsSelected = selectedTab === PromptTabs.GroupPrompts;
  const hasPrompts = prompts.length > 0;
  const hasGroups = (groups?.length || 0) > 0;
  const hasGroupPrompts = (groupPrompts?.length || 0) > 0;
  const isMember = groups?.some((group) => group.IsMember);
  const isGroupsHavingPrompts =
    isSuccess && groups?.some((group) => group.HasPrompts);

  const isRequestFailure =
    !isLoading &&
    isFailed &&
    ((isGroupPromptsSelected && (!hasGroupPrompts || !hasGroups)) ||
      (!isGroupPromptsSelected && !hasPrompts));

  const isNoSuggestedAndBookmarkPrompts =
    isSuccess && !hasPrompts && !isGroupPromptsSelected;

  const isNoGroupsForUser = isSuccess && isGroupPromptsSelected && !hasGroups;

  const isAnyPromptsDisplayed = isSuccess && hasPrompts;

  const isUserMemberOfGroupSelectedWithoutPrompts =
    isSuccess &&
    isGroupPromptsSelected &&
    hasGroups &&
    isMember &&
    !hasGroupPrompts;

  const isNoGroupsHavingPromptsForUser =
    isSuccess &&
    isGroupPromptsSelected &&
    hasGroups &&
    isMember &&
    !isGroupsHavingPrompts;

  return (
    <div aria-live="polite" aria-busy={isLoading}>
      {isNoSuggestedAndBookmarkPrompts && (
        <NoResults
          showBookmarkOnly={selectedTab === PromptTabs.SavedPrompts}
          hasBookmarkedPrompts={hasBookmarkedPrompts}
        />
      )}
      {isRequestFailure && (
        <div className={styles.container}>
          <div className={styles.text}>
            {localization.PromptsRequestFailed()}
          </div>
        </div>
      )}
      {isAnyPromptsDisplayed && (
        <div id={id} className={styles.srOnly}>
          {localization.PromptGridDescription()}
        </div>
      )}
      {isNoGroupsForUser && (
        <div className={styles.container}>
          <div className={styles.text}>
            {localization.PromptGridNoTeamsSuccessTitle()}
          </div>
          <img
            src="/images/0192-people-l-standard-192x192.webp"
            role="presentation"
            alt=""
          />
          <div
            className={styles.additionalText}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html:
                localizationWithPlaceholders.PromptGridNoTeamsSuccessDescription(
                  {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    learnMore: `<a href="https://support.microsoft.com/office/create-a-team-from-scratch-in-microsoft-teams-174adf5f-846b-4780-b765-de1a0a737e2b" target="_blank">${localization.PromptGridLearnMoreLinkLabel()}</a>`,
                  }
                ),
            }}
          ></div>
          <Button
            size="large"
            className={styles.actionButton}
            onClick={onExploreOtherPrompts}
          >
            {localization.PromptGridNoTeamsSuccessButtonLabel()}
          </Button>
        </div>
      )}
      {isMetaOsV2
        ? (isUserMemberOfGroupSelectedWithoutPrompts ||
            isNoGroupsHavingPromptsForUser) && (
            <NoResults isMetaOsV2={isMetaOsV2}></NoResults>
          )
        : (isUserMemberOfGroupSelectedWithoutPrompts ||
            isNoGroupsHavingPromptsForUser) &&
          (onSharePrompts ? (
            <div className={styles.container}>
              <div className={styles.text}>
                {localization.PromptGridTeamsNoPromptsOwnerSuccessTitle()}
              </div>
              <img
                src="/images/0762-ai-text-l-standard-192x192.webp"
                role="presentation"
                alt=""
              />
              <SharePromptText className={styles.additionalText} />
              <Button
                size="large"
                className={styles.actionButton}
                onClick={onSharePrompts}
                id={"link-to-app-no-prompts-shared"}
              >
                {localization.PromptGridTeamsNoPromptsOwnerSuccessButtonLabel()}
              </Button>
            </div>
          ) : (
            <div className={styles.container}>
              <div className={styles.text}>
                {localization.PromptGridTeamsNoPromptsOwnerSuccessTitle()}
              </div>
              <img
                src="/images/0762-ai-text-l-standard-192x192.webp"
                role="presentation"
                alt=""
              />
              <div className={styles.additionalText}>
                {localization.PromptGridTeamsNoPromptsOwnerSuccessDescriptionCannotShareV2()}
              </div>
              {!isTeamsApp() && (
                <Button
                  size="large"
                  className={styles.actionButton}
                  role="link"
                  as="a"
                  href={COPILOT_LAB_URL}
                  id={"link-to-app-no-prompts-shared"}
                >
                  {localization.PromptGridTeamsNoPromptsOwnerSuccessButtonLabelCannotShareV2()}
                </Button>
              )}
              {isTeamsApp() && (
                <Button
                  size="large"
                  className={styles.actionButton}
                  onClick={onGoToCopilotLabClicked}
                  role="link"
                  as="a"
                  id={"link-to-app-no-prompts-shared"}
                >
                  {localization.PromptGridTeamsNoPromptsOwnerSuccessButtonLabelCannotShareV2()}
                </Button>
              )}
            </div>
          ))}
    </div>
  );
};
